import React from "react";
import {
  Box,
  Breadcrumbs,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { CircleRounded } from "@mui/icons-material";
import RenderMarkdown from "../Answer/RenderMarkdown";
import { getFileIconImage } from "../../../../../../utils/fileUtils";
import { useThemeSettings } from "../../../../../../theme/ThemeSettingsProvider";

const useStyles = makeStyles()((theme) => ({
  docCard: {
    padding: 12,
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
  },
  docCardSelected: {
    borderColor:
      theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0",
    boxShadow: "0 4px 12px rgba(156, 39, 176, 0.15)",
  },
  contentPreview: {
    maxHeight: 40,
    overflow: "hidden",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: 30,
      background: `linear-gradient(transparent, ${theme.palette.background.default})`,
    },
  },
  fileIcon: {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.light,
    "&:hover": {
      backgroundColor: theme.palette.background.light,
    },
  },
}));

function RelatedDocs({
  message = { query: {}, answer: {}, answer_rd: null },
  setSelectedRelatedDoc,
  relatedDocsFromStreaming = null,
  selectedRelatedDoc,
}) {
  const theme = useTheme();
  const { mode } = useThemeSettings();
  const isDarkMode = mode === "dark";
  const { classes } = useStyles();
  const { query, answer_rd } = message;
  const relatedDocs =
    Array.isArray(relatedDocsFromStreaming) &&
    relatedDocsFromStreaming.length > 0
      ? relatedDocsFromStreaming
      : answer_rd
      ? answer_rd["content_object"]?.["related_docs"] || []
      : [];


  if (relatedDocs.length === 0) {
    return <Typography>No related docs found</Typography>;
  }

  return (
    <Box>
      {relatedDocs.map((doc, index) => {
        const { chunk_content, citations, section_title, file_name, file } =
          doc;
        const startPage = citations[0]?.page;
        const endPage = citations[citations.length - 1]?.page;

        // Use a combination of properties to determine uniqueness
        const isSelected =
          selectedRelatedDoc &&
          selectedRelatedDoc.section_title === doc.section_title &&
          selectedRelatedDoc.file_name === doc.file_name &&
          selectedRelatedDoc.chunk_content === doc.chunk_content;

        return (
          <Box key={index} onClick={() => setSelectedRelatedDoc(doc)} py={2} sx={{ cursor: "pointer" }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <IconButton className={classes.fileIcon}>
                {(() => {
                  const iconSrc = getFileIconImage(
                    (file_name || "").toLowerCase()
                  );
                  return iconSrc ? (
                    <img
                      src={iconSrc}
                      alt="file_icon"
                      style={{ width: 20, height: 20 }}
                    />
                  ) : null;
                })()}
              </IconButton>
              <Box>
                <Breadcrumbs separator="›" aria-label="breadcrumb">
                  {file &&
                    file.directory &&
                    file.directory.split("/").map((dir, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        color={
                          index === file.directory.split("/").length - 1
                            ? "text.secondary"
                            : "text.primary"
                        }>
                        {dir}
                      </Typography>
                    ))}
                </Breadcrumbs>
                <Typography variant="h6" color="text.primary">
                  {file_name}
                </Typography>
              </Box>
            </Box>
            <Box pl={5.5}>
              <Box className={`${classes.docCard} ${
                isSelected ? classes.docCardSelected : ""
              }`}>
                <Box display="flex" alignItems="center" gap={0.75}>
                  <Typography fontWeight={600} color="text.primary">
                    {section_title}
                  </Typography>
                  <CircleRounded
                    sx={{ fontSize: 3, color: "text.secondary", mx: 0.5 }}
                  />
                  <Typography fontWeight={600} color="text.secondary">
                    {startPage === endPage
                      ? `Pg. ${startPage}`
                      : `Pg. ${startPage}-${endPage}`}
                  </Typography>
                </Box>
                <Box mb={1} />
                <Box className={classes.contentPreview}>
                  <Box
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: "text.secondary",
                      "& table": {
                        borderCollapse: "collapse",
                        width: "100%",
                        fontSize: 11,
                      },
                      "& th, & td": {
                        border: `1px solid ${theme.palette.divider}`,
                        padding: "4px 6px",
                        textAlign: "left",
                      },
                      "& th": {
                        backgroundColor: theme.palette.background.light,
                        fontWeight: 600,
                      },
                      "& tr:nth-of-type(even)": {
                        backgroundColor: theme.palette.background.dark,
                      },
                      "& p": {
                        margin: "3px 0",
                      },
                      "& ul, & ol": {
                        paddingLeft: 16,
                        margin: "3px 0",
                      },
                      "& h1, & h2, & h3, & h4, & h5, & h6": {
                        fontSize: 13,
                        margin: "4px 0",
                      },
                    }}>
                    <RenderMarkdown
                      message={chunk_content}
                      singleLine={false}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default RelatedDocs;
